import PsychologyIcon from "@mui/icons-material/Psychology";
import SettingsIcon from "@mui/icons-material/Settings";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useState } from "react";

import { useFilters } from "../../../common/hooks/useFilters";
import { FeatureFlag } from "../../../common/posthog";
import { AVAILABLE_MODELS, AvailableModel, MODEL_TOKEN_LIMITS, supportsAdaptiveThinking, THINKING_MODE_TEMPERATURE, ThinkingModeEffortLevel } from "../../../services/claude";
import { Artifact, ArtifactType } from "../../../types/artifact";
import { useChatStore } from "../stores/chatStore";
import { ChatActions, ChatState, RowSelectionMode } from "../stores/chatTypes";
import { buildInteractionFilter, extractInteractionIds } from "../utils/filters";
import ArtifactTypeSelector from "./ArtifactTypeSelector";

const MIN_INPUT_TOKENS = 1;
const MIN_MAX_TOKENS = 0;
const MIN_TEMPERATURE = 0;
const MAX_TEMPERATURE = 1;
const TEMPERATURE_STEP = 0.1;
const TEMPERATURE_FRACTION_DIGITS = 2;
const TOKEN_STEP_SMALL = 100;
const TOKEN_STEP_LARGE = 1000;
const DECIMAL_RADIX = 10;
const EMPTY_INTERACTIONS = 0;

const EffortSelector = ({ effort, thinkingMode, onChange }: {
  effort: ThinkingModeEffortLevel;
  thinkingMode: boolean;
  onChange: (event: SelectChangeEvent<string>) => void;
}): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color={!thinkingMode ? "text.disabled" : "text.secondary"}
      sx={{ mb: 1, display: "block" }}
    >
      Effort
    </Typography>
    <Tooltip title="Controls how much thinking effort Claude applies. Higher effort means deeper reasoning but more tokens and latency.">
      <FormControl fullWidth size="small" disabled={!thinkingMode}>
        <Select
          value={effort}
          onChange={onChange}
          inputProps={{ ["aria-label"]: "Effort Level" }}
        >
          <MenuItem value="low"><Typography variant="body2">Low</Typography></MenuItem>
          <MenuItem value="medium"><Typography variant="body2">Medium</Typography></MenuItem>
          <MenuItem value="high"><Typography variant="body2">High</Typography></MenuItem>
        </Select>
      </FormControl>
    </Tooltip>
  </Box>
);

interface BudgetTokensInputProps {
  budgetTokens: number;
  thinkingMode: boolean;
  modelLimits: { minBudgetTokens: number; maxOutputTokens: number };
  modelName: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const buildBudgetHelperText = (modelLimits: { minBudgetTokens: number }, modelName: string): string =>
  "Portion of Max Tokens for Claude's internal reasoning. Only active with Thinking Mode enabled "
  + `(min ${modelLimits.minBudgetTokens.toLocaleString()} for ${modelName})`;

const BudgetTokensInput = ({ budgetTokens, thinkingMode, modelLimits, modelName, onChange }: BudgetTokensInputProps): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color={!thinkingMode ? "text.disabled" : "text.secondary"}
      sx={{ mb: 1, display: "block" }}
    >
      Budget Tokens
    </Typography>
    <Tooltip title={
      "Maximum tokens for Claude's thinking process. Must be less than Max Tokens"
      + ` (min ${modelLimits.minBudgetTokens.toLocaleString()} for ${modelName})`
    }>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={budgetTokens}
        onChange={onChange}
        helperText={buildBudgetHelperText(modelLimits, modelName)}
        FormHelperTextProps={{ sx: { ml: 0 } }}
        inputProps={{
          min: modelLimits.minBudgetTokens,
          max: modelLimits.maxOutputTokens,
          step: TOKEN_STEP_SMALL,
          ["aria-label"]: "Budget Tokens"
        }}
        disabled={!thinkingMode}
      />
    </Tooltip>
  </Box>
);

const styles = {
  container: {
    px: 2,
    pt: 2,
    pb: 0,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    bgcolor: "#FFFFFF"
  },
  accordion: {
    boxShadow: "none",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: "4px"
  },
  accordionSummary: {
    minHeight: 40,
    ["& .MuiAccordionSummary-content"]: { margin: "4px 0" }
  },
  controlRow: {
    display: "flex",
    alignItems: "center",
    mb: 1.5
  },
  controlRowInner: {
    display: "flex",
    alignItems: "center",
    width: "100%"
  }
} as const;

interface ChatControlsProps {
  selectedRows: Artifact[];
  loading: boolean;
  rowSelectionMode: RowSelectionMode;
}

interface ThinkingModeToggleProps {
  thinkingMode: boolean;
  isAdaptive: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const buildThinkingTooltip = (isAdaptive: boolean): string =>
  isAdaptive
    ? "Enable adaptive thinking — Claude decides how deeply to reason based on query complexity and effort level. Temperature is fixed at 1."
    : "Enable to see Claude's thinking process. When enabled, temperature is fixed at 1.";

const ThinkingModeToggle = ({ thinkingMode, isAdaptive, onChange }: ThinkingModeToggleProps): React.ReactElement => (
  <Box sx={styles.controlRow}>
    <Tooltip title={buildThinkingTooltip(isAdaptive)}>
      <Box sx={styles.controlRowInner}>
        <PsychologyIcon fontSize="small" sx={{ color: "text.secondary", mr: 1 }} />
        <FormGroup>
          <FormControlLabel
            control={<Switch checked={thinkingMode} onChange={onChange} size="small" />}
            label={<Typography variant="caption">Thinking Mode</Typography>}
          />
        </FormGroup>
      </Box>
    </Tooltip>
  </Box>
);

interface ModelSelectorProps {
  model: AvailableModel;
  onChange: (event: SelectChangeEvent<string>) => void;
}

const ModelSelector = ({ model, onChange }: ModelSelectorProps): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>Model</Typography>
    <Tooltip title="Select which Claude model to use for this conversation">
      <FormControl fullWidth size="small">
        <Select
          value={model}
          onChange={onChange}
          displayEmpty
          inputProps={{ ["aria-label"]: "Select Model" }}
        >
          {AVAILABLE_MODELS.map((modelOption) => (
            <MenuItem key={modelOption.id} value={modelOption.id}>
              <Typography variant="body2">{modelOption.name}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  </Box>
);

interface TemperatureControlProps {
  thinkingMode: boolean;
  temperature: number;
  onChange: (event: Event, newValue: number | number[]) => void;
}

const TemperatureControl = ({ thinkingMode, temperature, onChange }: TemperatureControlProps): React.ReactElement => {
  const displayValue = thinkingMode ? THINKING_MODE_TEMPERATURE : temperature;
  return (
    <Box sx={styles.controlRow}>
      <Tooltip title="Controls randomness: Lower values give more consistent, deterministic responses. Higher values make responses more creative and varied.">
        <Box sx={styles.controlRowInner}>
          <ThermostatIcon fontSize="small" sx={{ color: thinkingMode ? "text.disabled" : "text.secondary", mr: 1 }} />
          <Typography variant="caption" sx={{ minWidth: 110, color: thinkingMode ? "text.disabled" : "text.secondary" }}>
            Temperature: {displayValue.toFixed(TEMPERATURE_FRACTION_DIGITS)}
          </Typography>
          <Slider
            value={displayValue}
            onChange={onChange}
            aria-label="Temperature"
            min={MIN_TEMPERATURE}
            max={MAX_TEMPERATURE}
            step={TEMPERATURE_STEP}
            valueLabelDisplay="auto"
            sx={{ ml: 1, flex: 1 }}
            disabled={thinkingMode}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

interface MaxTokensInputProps {
  maxTokens: number;
  modelLimits: { maxOutputTokens: number };
  modelName: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const buildMaxTokensHelperText = (modelLimits: { maxOutputTokens: number }, modelName: string): string =>
  `Total cap on Claude's entire response, including both thinking and text output (up to ${modelLimits.maxOutputTokens.toLocaleString()} for ${modelName})`;

const MaxTokensInput = ({ maxTokens, modelLimits, modelName, onChange }: MaxTokensInputProps): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>Max Tokens</Typography>
    <Tooltip title={
      "Maximum number of tokens in Claude's response, including thinking and text output"
      + ` (up to ${modelLimits.maxOutputTokens.toLocaleString()} for ${modelName})`
    }>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={maxTokens}
        onChange={onChange}
        helperText={buildMaxTokensHelperText(modelLimits, modelName)}
        FormHelperTextProps={{ sx: { ml: 0 } }}
        inputProps={{
          min: MIN_MAX_TOKENS,
          max: modelLimits.maxOutputTokens,
          step: TOKEN_STEP_SMALL,
          ["aria-label"]: "Max Tokens"
        }}
      />
    </Tooltip>
  </Box>
);

interface MaxInputTokensInputProps {
  maxInputTokens: number;
  modelLimits: { maxInputTokens: number };
  modelName: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

const MaxInputTokensInput = ({ maxInputTokens, modelLimits, modelName, onChange, onBlur }: MaxInputTokensInputProps): React.ReactElement => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>Max Input Tokens</Typography>
    <Tooltip title={
      "Maximum tokens for the input context window"
      + ` (up to ${modelLimits.maxInputTokens.toLocaleString()} for ${modelName})`
    }>
      <TextField
        type="number"
        size="small"
        fullWidth
        value={maxInputTokens}
        onChange={onChange}
        onBlur={onBlur}
        inputProps={{
          min: MIN_INPUT_TOKENS,
          max: modelLimits.maxInputTokens,
          step: TOKEN_STEP_LARGE,
          ["aria-label"]: "Max Input Tokens"
        }}
      />
    </Tooltip>
  </Box>
);

type ChatStoreValue = ChatState & ChatActions;

interface RefreshContextParams {
  selectedRows: Artifact[];
  filters: ReturnType<typeof useFilters>["filters"];
  rowSelectionMode: RowSelectionMode;
  includedArtifactTypes: ArtifactType[];
  loadContext: ChatStoreValue["loadContext"];
}

const buildRefreshContext = (params: RefreshContextParams) =>
  (artifactTypesOverride?: ArtifactType[]): void => {
    const { selectedRows, filters, rowSelectionMode, includedArtifactTypes, loadContext } = params;
    const interactionIds = extractInteractionIds(selectedRows);
    if (interactionIds.length > EMPTY_INTERACTIONS) {
      const contextFilter = buildInteractionFilter(
        filters,
        rowSelectionMode,
        artifactTypesOverride ?? includedArtifactTypes
      );
      loadContext(interactionIds, true, contextFilter);
    }
  };

interface ChangeHandlers {
  handleTemperatureChange: (event: Event, newValue: number | number[]) => void;
  handleThinkingModeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxTokensChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleEffortChange: (event: SelectChangeEvent<string>) => void;
  handleBudgetTokensChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxInputTokensChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxInputTokensBlur: () => void;
  handleModelChange: (event: SelectChangeEvent<string>) => void;
  handleArtifactTypesChange: (newTypes: ArtifactType[]) => void;
}

interface ChangeHandlerDeps {
  store: ChatStoreValue;
  modelLimits: { maxOutputTokens: number; minBudgetTokens: number; maxInputTokens: number };
  refreshContext: (artifactTypesOverride?: ArtifactType[]) => void;
}

const buildChangeHandlers = ({ store, modelLimits, refreshContext }: ChangeHandlerDeps): ChangeHandlers => ({
  handleTemperatureChange: (_event, newValue) => store.setTemperature(newValue as number),
  handleThinkingModeChange: (event) => store.setThinkingMode(event.target.checked),
  handleMaxTokensChange: (event) => {
    const value = parseInt(event.target.value, DECIMAL_RADIX);
    if (!isNaN(value) && value >= MIN_MAX_TOKENS && value <= modelLimits.maxOutputTokens) {
      store.setMaxTokens(value);
    }
  },
  handleEffortChange: (event) => store.setEffort(event.target.value as ThinkingModeEffortLevel),
  handleBudgetTokensChange: (event) => {
    const value = parseInt(event.target.value, DECIMAL_RADIX);
    if (!isNaN(value) && value >= modelLimits.minBudgetTokens && value <= modelLimits.maxOutputTokens) {
      store.setBudgetTokens(value);
    }
  },
  handleMaxInputTokensChange: (event) => {
    const value = parseInt(event.target.value, DECIMAL_RADIX);
    if (!isNaN(value) && value >= MIN_INPUT_TOKENS && value <= modelLimits.maxInputTokens) {
      store.setMaxInputTokens(value);
    }
  },
  handleMaxInputTokensBlur: () => refreshContext(),
  handleModelChange: (event) => {
    const newModel = event.target.value as AvailableModel;
    const newLimits = MODEL_TOKEN_LIMITS[newModel];
    store.setModel(newModel);
    store.setMaxTokens(newLimits.maxOutputTokens);
    store.setMaxInputTokens(newLimits.maxInputTokens);
    refreshContext();
  },
  handleArtifactTypesChange: (newTypes) => {
    store.setIncludedArtifactTypes(newTypes);
    refreshContext(newTypes);
  }
});

interface ThinkingSectionProps {
  store: ChatStoreValue;
  isAdaptive: boolean;
  modelLimits: { maxOutputTokens: number; minBudgetTokens: number; maxInputTokens: number };
  modelName: string;
  handlers: ChangeHandlers;
}

const ThinkingSection = ({ store, isAdaptive, modelLimits, modelName, handlers }: ThinkingSectionProps): React.ReactElement => (
  <FeatureFlag flag="extended-thinking-mode-in-chat" fallback={null}>
    <ThinkingModeToggle
      thinkingMode={store.thinkingMode}
      isAdaptive={isAdaptive}
      onChange={handlers.handleThinkingModeChange}
    />
    <ModelSelector model={store.model} onChange={handlers.handleModelChange} />
    <TemperatureControl
      thinkingMode={store.thinkingMode}
      temperature={store.temperature}
      onChange={handlers.handleTemperatureChange}
    />
    <MaxTokensInput
      maxTokens={store.maxTokens}
      modelLimits={modelLimits}
      modelName={modelName}
      onChange={handlers.handleMaxTokensChange}
    />
    {isAdaptive ? (
      <EffortSelector effort={store.effort} thinkingMode={store.thinkingMode} onChange={handlers.handleEffortChange} />
    ) : (
      <BudgetTokensInput
        budgetTokens={store.budgetTokens}
        thinkingMode={store.thinkingMode}
        modelLimits={modelLimits}
        modelName={modelName}
        onChange={handlers.handleBudgetTokensChange}
      />
    )}
    <MaxInputTokensInput
      maxInputTokens={store.maxInputTokens}
      modelLimits={modelLimits}
      modelName={modelName}
      onChange={handlers.handleMaxInputTokensChange}
      onBlur={handlers.handleMaxInputTokensBlur}
    />
  </FeatureFlag>
);

const ChatControls = ({ selectedRows, loading, rowSelectionMode }: ChatControlsProps): React.ReactElement => {
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const { filters } = useFilters();
  const store = useChatStore();

  const isAdaptive = supportsAdaptiveThinking(store.model);
  const modelLimits = MODEL_TOKEN_LIMITS[store.model];
  const modelName = AVAILABLE_MODELS.find((m) => m.id === store.model)?.name ?? store.model;

  const refreshContext = buildRefreshContext({
    selectedRows,
    filters,
    rowSelectionMode,
    includedArtifactTypes: store.includedArtifactTypes,
    loadContext: store.loadContext
  });

  const handlers = buildChangeHandlers({ store, modelLimits, refreshContext });

  return (
    <Box sx={styles.container}>
      <Accordion
        expanded={settingsExpanded}
        onChange={() => setSettingsExpanded(!settingsExpanded)}
        sx={styles.accordion}
        disabled={store.viewingPreviousSession}
      >
        <AccordionSummary
          expandIcon={<SettingsIcon fontSize="small" />}
          aria-controls="chat-settings-content"
          id="chat-settings-header"
          sx={styles.accordionSummary}
        >
          <Typography variant="caption" color="text.secondary">Settings</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 1 }}>
          <ThinkingSection
            store={store}
            isAdaptive={isAdaptive}
            modelLimits={modelLimits}
            modelName={modelName}
            handlers={handlers}
          />
          <Divider sx={{ my: 1.5 }} />
          <ArtifactTypeSelector
            selectedTypes={store.includedArtifactTypes}
            onChange={handlers.handleArtifactTypesChange}
            disabled={loading}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ChatControls;
